import type { ColumnSort } from '@tanstack/react-table';
import { useCallback, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Pagination } from '~/components/Pagination';
import { useArtistTracksQuery } from '~/modules/artist-roster/hooks/useArtistTracksQuery';
import { useReleasesUrlParams } from '~/modules/artist-roster/hooks/useReleasesUrlParams';
import type { ArtistDetails } from '~/modules/artist-roster/lib/types';

import { ArtistSearchBar } from '../ArtistSearchBar/ArtistSearchBar';
import { DEFAULT_PAGE_SIZE } from '../constants';

interface PreReleaseTableHeaderProps {
  uaid: number | null;
  artistData?: ArtistDetails;
  columnSort?: ColumnSort;
  scrollUpCallback?: () => void;
}

export function PreReleaseTableHeader({
  uaid,
  artistData,
  columnSort,
  scrollUpCallback,
}: PreReleaseTableHeaderProps) {
  const { t } = useTranslation('roster');

  const { pageNumber, setPageNumber, filters, updateFilters } =
    useReleasesUrlParams();
  const { releaseType, searchText } = filters;

  const { data: tracksData, error } = useArtistTracksQuery({
    uaid,
    columnSort,
    pageNumber,
    releaseType,
    searchText,
    isReleased: false,
  });

  const totalTracks = tracksData?.pagination.total ?? 0;

  const handleSetPageNumber = useCallback(
    (pageNum: number) => {
      setPageNumber(pageNum);
    },
    [setPageNumber],
  );

  useEffect(() => {
    scrollUpCallback?.();
  }, [scrollUpCallback, pageNumber]);

  const handleSubmitSearch = useCallback(
    (searchValue: string) => {
      updateFilters({ searchText: searchValue });
    },
    [updateFilters],
  );

  const handleSearchInputClear = useCallback(() => {
    updateFilters({ searchText: undefined });
  }, [updateFilters]);

  if (!artistData) {
    return null;
  }

  if (error) {
    return <div>{t('trackList.errors.loadingTracks')}</div>;
  }

  return (
    <>
      <div className="mb-md flex w-full">
        <div className="flex w-full flex-col gap-xs">
          <h2 className="text-headline-sm font-bold">
            {t('preReleases.header')}
          </h2>
          <p className="text-caption-md">
            <Trans
              t={t}
              i18nKey="preReleases.subtitle"
              components={{
                span: <span className="font-semibold" />,
                br: <br />,
              }}
            />
          </p>
        </div>
        <div className="inline-flex items-center space-x-md text-caption-md">
          {totalTracks > DEFAULT_PAGE_SIZE ? (
            <Pagination
              page={pageNumber}
              total={totalTracks}
              perPage={DEFAULT_PAGE_SIZE}
              entity="tracks"
              setPageNumber={handleSetPageNumber}
              showAsLink={true}
            />
          ) : null}
        </div>
      </div>
      <div className="flex gap-md pb-md" role="search">
        <ArtistSearchBar
          initialSearchText={searchText}
          placeholder={t('filters.searchForTrack')}
          onSubmit={handleSubmitSearch}
          onClear={handleSearchInputClear}
        />
      </div>
    </>
  );
}

PreReleaseTableHeader.displayName = 'PreReleaseTableHeader';
